import { useState, useEffect } from 'react';
import { useSearchParams, useNavigate, Link } from 'react-router-dom';
import { Card, CardContent } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Mail, RefreshCw, CheckCircle, ArrowLeft, Clock, AlertCircle } from 'lucide-react';
import { toast } from 'sonner';
import api from '@/lib/api';

export default function VerificacionPendiente() {
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const email = searchParams.get('email') || '';
  const userId = searchParams.get('uid') || '';
  const [enviando, setEnviando] = useState(false);
  const [countdown, setCountdown] = useState(0);

  useEffect(() => {
    if (countdown > 0) {
      const t = setTimeout(() => setCountdown(c => c - 1), 1000);
      return () => clearTimeout(t);
    }
  }, [countdown]);

  const reenviar = async () => {
    if (countdown > 0) return;
    setEnviando(true);
    try {
      await api.post('/auth/enviar-verificacion', { email, user_id: userId });
      toast.success('Correo de verificacion reenviado');
      setCountdown(60);
    } catch (e) {
      const msg = e.response?.data?.detail || 'Error al reenviar';
      toast.error(msg);
    } finally {
      setEnviando(false);
    }
  };

  return (
    <div className="min-h-screen bg-slate-950 flex items-center justify-center px-4" data-testid="verificacion-pendiente-page">
      <div className="w-full max-w-md animate-fadeIn">
        <Card className="bg-slate-900/80 border-slate-800 backdrop-blur-xl">
          <CardContent className="p-8 text-center">
            <div className="w-16 h-16 rounded-full bg-amber-500/10 flex items-center justify-center mx-auto mb-6">
              <Mail className="w-8 h-8 text-amber-500" />
            </div>
            <h1 className="font-barlow text-2xl font-bold text-slate-50 uppercase mb-2">Verifica tu correo</h1>
            <p className="text-slate-400 text-sm mb-6">
              Te enviamos un enlace de verificacion a:<br />
              <span className="text-amber-400 font-medium">{email}</span>
            </p>

            <div className="bg-slate-800/50 rounded-sm p-4 mb-6 text-left space-y-2">
              <div className="flex items-start gap-2">
                <CheckCircle className="w-4 h-4 text-emerald-400 mt-0.5 shrink-0" />
                <p className="text-xs text-slate-400">Revisa tu bandeja de entrada y spam</p>
              </div>
              <div className="flex items-start gap-2">
                <Clock className="w-4 h-4 text-amber-400 mt-0.5 shrink-0" />
                <p className="text-xs text-slate-400">El enlace expira en 30 minutos</p>
              </div>
              <div className="flex items-start gap-2">
                <AlertCircle className="w-4 h-4 text-cyan-400 mt-0.5 shrink-0" />
                <p className="text-xs text-slate-400">Una vez verificado, podras iniciar sesion</p>
              </div>
            </div>

            <Button onClick={reenviar} disabled={enviando || countdown > 0}
              variant="outline"
              className="w-full border-slate-700 text-slate-300 hover:bg-slate-800 h-11 mb-3"
              data-testid="reenviar-verificacion-btn">
              <RefreshCw className={`w-4 h-4 mr-2 ${enviando ? 'animate-spin' : ''}`} />
              {countdown > 0 ? `Reenviar en ${countdown}s` : enviando ? 'Enviando...' : 'Reenviar correo de verificacion'}
            </Button>

            <Link to="/login" className="inline-flex items-center gap-1 text-sm text-amber-500 hover:text-amber-400" data-testid="volver-login-link">
              <ArrowLeft className="w-3.5 h-3.5" /> Ya verifique mi correo, ir a login
            </Link>
          </CardContent>
        </Card>
      </div>
    </div>
  );
}
